/* eslint-disable no-underscore-dangle */
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Container,
  Paper,
  Breadcrumbs,
  Link as MuiLink,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Tab,
  Tabs,
  Skeleton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import { styled } from '@mui/material/styles';
import { useAppSelector } from 'renderer/shared/state/hooks';
import { selectUserRole } from 'renderer/features/auth/state/userSelectors';
import AddIcon from '@mui/icons-material/Add';
import { formatCurrency } from 'renderer/shared/formatters/formatters';
import _ from 'lodash';
import { useListProductsQuery } from '../services/products.endpoints';
import productThumbnails from '../constants/productThumbnails';
import PurchaseDialog from './PurchaseDialog';
import { categoryIdToName } from '../constants/storeTabs';
import {
  useGetCartQuery,
  useAddToCartMutation,
} from '../../payments-page/services/cart.endpoints';

const TabNavigation = () => {
  const { categoryId } = useParams();
  const currentTab = categoryId ?? 'video-plugins';

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <Tabs
        value={currentTab}
        TabIndicatorProps={{
          style: {
            backgroundColor: '#1976D2', // Blue underline
            height: 3,
          },
        }}
        textColor="inherit"
      >
        {[
          { label: 'VIDEO PLUGINS', value: 'video-plugins', to: '/' },
          {
            label: 'SUBSCRIPTIONS',
            value: 'subscriptions',
            to: '/store/subscriptions',
          },
          {
            label: 'BUNDLES',
            value: 'bundles',
            to: '/store/bundles',
          },
          { label: 'UPGRADES', value: 'upgrades', to: '/store/upgrades' },
          {
            label: 'PHOTO PLUGINS',
            value: 'photo-plugins',
            to: '/store/photo-plugins',
          },
        ].map((tab) => (
          <Tab
            key={tab.value}
            label={tab.label}
            value={tab.value}
            to={tab.to}
            component={Link}
            sx={{
              fontSize: 18,
              fontWeight: currentTab === tab.value ? 'bold' : 'normal',
              color: currentTab === tab.value ? '#1976D2' : 'text.primary',
              textTransform: 'none',
              mx: 1,
            }}
          />
        ))}
      </Tabs>
    </Box>
  );
};

const StyledCardOverlay = styled(Box)(() => ({
  position: 'absolute',
  top: 0,
  bottom: 0,
  right: 0,
  left: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.7)',
  color: 'white',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: 0,
  transition: 'opacity 0.3s ease-in-out',
  '&:hover': {
    opacity: 1,
  },
}));

const StyledCardMedia = styled(CardMedia)(({ theme }) => ({
  height: 0,
  paddingTop: '100%', // 1:1 Aspect Ratio
  backgroundSize: 'contain', // Contain the image within the element
  position: 'relative', // Needed for after pseudo-element
  '&:after': {
    // Pseudo-element for the bottom border
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    borderBottom: `1px solid ${theme.palette.divider}`,
    boxShadow: '0px 1px 1px rgba(0,0,0,0.5)', // Darker box shadow effect
    width: 'calc(100% - 16px)', // Adjust width if needed
    margin: '0 8px', // Center the shadow effect with a margin
  },
}));

const StyledCard = styled(Card)(() => ({
  maxWidth: 220,
  margin: 'auto', // Centers the card within the Grid item
  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out', // Smooth transition for transform and shadow
  '&:hover': {
    transform: 'scale(1.05)', // Scales up the card to 105% of its original size
    // boxShadow: `0 6px 12px 0 ${theme.palette.cardShadow}`, // Adjust box shadow as needed
    cursor: 'pointer',
    // Rest of your hover styles
    '& $StyledCardOverlay': {
      // Update the reference to your actual overlay component name if different
      opacity: 1,
    },
  },
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  position: 'relative', // Needed for absolute positioning of the CardActions
  // '&:hover': {
  //   boxShadow: `0 6px 12px 0 ${theme.palette.cardShadow}`,
  // },
  '& .MuiCardMedia-root': {
    paddingTop: '100%', // 1:1 Aspect Ratio
  },
}));

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  textAlign: 'center',
  '&:last-child': {
    paddingBottom: theme.spacing(1), // Reduce padding at the bottom of CardContent
  },
  '& .MuiTypography-body2': {
    display: '-webkit-box',
    WebkitLineClamp: 2, // Limit text to 2 lines
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  '& .MuiTypography-h5': {
    fontSize: '1rem',
    fontWeight: theme.typography.fontWeightMedium,
  },
  '& .MuiTypography-h6': {
    fontSize: '1.25rem',
    color: theme.palette.primary.main,
    fontWeight: 'bold',
    // marginBottom: theme.spacing(2), // Ensure consistent spacing above the card actions
  },
}));

const CornerBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 8,
  left: 0,
  backgroundColor: '#17a2b8',
  color: '#fff',
  fontSize: '0.95rem',
  fontWeight: 'bold',
  padding: '2px 6px',
  // borderRadius: '4px',
  zIndex: 10,
}));

const ProductsPage = () => {
  const navigate = useNavigate();
  const [dialogHostApp, setDialogHostApp] = useState<{
    id: string;
    displayName: string;
    version: string;
  } | null>(null);
  // const [sortValue, setSortValue] = useState('priceLowHigh');
  const [sortValue, setSortValue] = useState('default');
  // const [category, setCategory] = useState('Video Plugin');
  const [searchTerm, setSearchTerm] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(
    null,
  );
  const { categoryId } = useParams();
  const category = categoryIdToName[categoryId ?? ''] ?? 'Video Plugin';

  //  const [addToCart] = useAddToCartMutation();

  const handleSortChange = (event: any) => {
    setSortValue(event.target.value);
  };

  const role = useAppSelector(selectUserRole);
  const {
    data: products,
    isLoading,
    error,
  } = useListProductsQuery({ includeHidden: role === 'admin' });

  const sortAndFilterProducts = () => {
    const productsCopy = _.cloneDeep(products);

    let filtered =
      productsCopy &&
      productsCopy
        .filter((product) => product.category === category)
        .filter((product) =>
          product.name.toLocaleLowerCase().includes(searchTerm.toLowerCase()),
        );

    // Ensure filtered is always an array
    filtered = filtered || [];

    if (sortValue === 'priceLowHigh') {
      filtered = filtered.sort((a, b) => a.startingPrice - b.startingPrice);
    } else if (sortValue === 'priceHighLow') {
      filtered = filtered.sort((a, b) => b.startingPrice - a.startingPrice);
    }

    return filtered;
  };

  const sortedFilteredProducts = sortAndFilterProducts();

  const [addToCart] = useAddToCartMutation();

  const handlePurchaseClick = async (
    productId: string,
    productCategory: string,
    productName: string,
    product: any,
  ) => {
    const isSubscription = productCategory === 'Subscription';

    if (!isSubscription) {
      // Non-subscription logic remains unchanged
      if (
        productCategory === 'Video Plugin' &&
        !productName.toLowerCase().includes('anarchy complete bundle')
      ) {
        const host =
          product.supportedHostApplications?.length === 1
            ? product.supportedHostApplications[0]
            : null;
        setDialogHostApp(host);
        setSelectedProductId(productId);
        setModalOpen(true);
        return;
      }

      navigate(`/products/${productId}`);
      return;
    }

    const isAllAccess = productName.toLowerCase().includes('all access');
    const isOneHostApp = productName.toLowerCase().includes('one host');

    const platform = product.supportedPlatforms?.[0] ?? 'Windows';
    const hostApplication = isAllAccess
      ? product.supportedHostApplications?.[0] ?? null
      : null;

    try {
      await addToCart({
        productId,
        quantity: 1,
        platform,
        hostApplication,
      });

      navigate('/cart/1');
    } catch (err) {
      console.error('❌ Failed to add subscription to cart:', err);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedProductId(null);
    setDialogHostApp(null); // clear for next time
  };

  const handleNavigate = (path: string) => {
    setModalOpen(false);
    navigate(path);
  };

  const getDisplayedPrice = (product: any) => {
    const now = new Date();
    const saleValid =
      product.sale &&
      product.sale.salePrice &&
      new Date(product.sale.until) > now;

    const displayPrice = saleValid
      ? product.sale.salePrice
      : product.startingPrice;
    const formatted = formatCurrency(displayPrice);

    return product.category === 'Subscription'
      ? `${formatted}/year`
      : formatted;
  };

  return (
    <Container maxWidth="lg" sx={{ px: 0, pb: 4 }}>
      {/* Breadcrumbs */}
      {/* <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <MuiLink underline="hover" color="inherit" href="/">
          Home
        </MuiLink>
        <Typography color="text.primary">Store</Typography>
      </Breadcrumbs> */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1,
        }}
      >
        <Typography variant="h4">Store</Typography>
        {role === 'admin' && (
          <Link to="/products/add" style={{ textDecoration: 'none' }}>
            <Button variant="contained" color="primary" startIcon={<AddIcon />}>
              Add a New Product
            </Button>
          </Link>
        )}
      </Box>
      <TabNavigation />
      <Box
        sx={{
          mb: 4,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Paper
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            p: 1.5,
            mb: 2,
            width: '100%',
          }}
        >
          <TextField
            id="search-products"
            placeholder="Search Product"
            type="search"
            variant="outlined"
            size="small"
            sx={{
              mr: 2,
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="sort-products-label">Sort by</InputLabel>
            <Select
              labelId="sort-products-label"
              id="sort-products"
              value={sortValue}
              label="Sort by"
              onChange={handleSortChange}
              displayEmpty
            >
              <MenuItem value="default">Default</MenuItem>
              <MenuItem value="priceLowHigh">Price: Low to High</MenuItem>
              <MenuItem value="priceHighLow">Price: High to Low</MenuItem>
            </Select>
          </FormControl>
        </Paper>
      </Box>
      <Grid container spacing={4}>
        {isLoading &&
          Array.from({ length: 5 }, (val, i) => (
            <Grid item key={i} xs={12} sm={3}>
              <Skeleton
                variant="rounded"
                width={250}
                height={250}
                data-testid="products-loading"
              />
            </Grid>
          ))}
        {error && (
          <Typography>
            Error getting products {'status' in error && error.status}
          </Typography>
        )}
        {sortedFilteredProducts && sortedFilteredProducts.length === 0 && (
          <Typography variant="h2">No products found.</Typography>
        )}
        {(sortedFilteredProducts &&
          sortedFilteredProducts.map((product) => (
            <Grid item xs={12} sm={6} md={3} key={product._id}>
              <StyledCard elevation={4}>
                <Link
                  to={`/products/${product._id}`}
                  style={{ textDecoration: 'none' }}
                >
                  <Box position="relative">
                    {product.priority && product.showBadge && (
                      <CornerBadge>NEW</CornerBadge>
                    )}
                    <StyledCardMedia
                      image={productThumbnails.get(product.productId) ?? ''}
                      title={product.name}
                    />
                  </Box>
                  <StyledCardContent>
                    <Typography variant="h5" component="h2" noWrap>
                      {product.name}
                    </Typography>
                    <Typography variant="h6" color="primary">
                      {getDisplayedPrice(product)}
                    </Typography>
                  </StyledCardContent>
                  <StyledCardOverlay>
                    <Button
                      variant="contained"
                      sx={{
                        backgroundColor: '#17a2b8',
                        '&:hover': {
                          backgroundColor: '#17a2b8',
                          opacity: 0.9,
                        },
                        mb: 2,
                        position: 'relative',
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        handlePurchaseClick(
                          product._id,
                          product.category,
                          product.name,
                          product,
                        );
                      }}
                    >
                      Purchase
                    </Button>
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(`/products/${product._id}`);
                      }}
                    >
                      See More
                    </Button>
                  </StyledCardOverlay>
                </Link>
              </StyledCard>
            </Grid>
          ))) || <div>No products found</div>}
      </Grid>
      <PurchaseDialog
        hostApp={dialogHostApp}
        platform=""
        products={products as any[]}
        open={modalOpen}
        onClose={handleCloseModal}
        onNavigate={handleNavigate}
        productId={selectedProductId}
      />
    </Container>
  );
};

export default ProductsPage;
